import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Check, Trash2, X } from 'lucide-react';
import { HelmetProvider } from 'react-helmet-async';

import { useTodoLists } from './hooks/useTodos';
import { todoApi } from './api/todoApi';
import { UserProvider, useUser } from './contexts/UserContext';
import { DialogProvider, useDialog } from './contexts/DialogContext';
import { SignalRProvider } from './contexts/SignalRContext';
import { ToastProvider } from './contexts/ToastContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { PremiumProvider } from './contexts/PremiumContext';
import { TodoFilterProvider } from './contexts/TodoFilterContext';
import { AuthGuard } from './components/AuthGuard';
import { NotificationPanel } from './components/NotificationPanel';
import { SettingsPage } from './components/SettingsPage';
import { NewPasswordForm } from './components/NewPasswordForm';
import { PublicTodoList } from './components/PublicTodoList';
import { PublicNoteView } from './components/PublicNoteView';
import { useNotifications } from './hooks/useNotifications';
import { usePersistedState } from './hooks/usePersistedState';
import { useReceivedInvitations } from './hooks/useReceivedInvitations';
import { ReceivedInvitationsWidget } from './components/ReceivedInvitationsWidget';
import { useTheme } from './hooks/useTheme';
import { pwaService } from './services/pwaService';
import { Notification, TaskNotification } from './types/notifications';
import { Modal } from './components/Modal';
import { CreateListForm } from './components/CreateListForm';
import { EditListForm } from './components/EditListForm';
import { ListInfoDialog } from './components/ListInfoDialog';
import { WelcomeSetup } from './components/WelcomeSetup';
import { AppLayout } from './components/AppLayout';
import { ModeRenderer } from './components/ModeRenderer';
import { FeedbackModal, FeedbackData } from './components/FeedbackModal';
import { submitFeedback } from './api/feedbackApi';
import { Loader } from '@mantine/core';
import './App.css';

function AppContent() {
  const { currentUser } = useUser();
  const { alert } = useDialog();
  const navigate = useNavigate();
  
  // Initialize theme system
  useTheme();
  const queryClient = useQueryClient();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [navigationMessage] = useState<string|null>(null);
  const [showCreateListForm, setShowCreateListForm] = useState(false);
  const [showEditListForm, setShowEditListForm] = useState(false);
  const [showListInfoDialog, setShowListInfoDialog] = useState(false);
  const [showWelcomeSetup, setShowWelcomeSetup] = useState(false);
  const [createListData, setCreateListData] = useState<{ name: string; description?: string; color?: string } | null>(null);
  const [editListData, setEditListData] = useState<{ name: string; description?: string; color?: string; isShared?: boolean; sharedWith?: string[]; isPublic?: boolean } | null>(null);
  const [newPublicUrl, setNewPublicUrl] = useState<string | null>(null);
  
  // Add filter state for TodoList with localStorage persistence
  const [todoFilters, setTodoFilters] = usePersistedState<{ assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }>('symphonytodo-filters', {
    assignee: 'any',
    list: 'all',
    completed: 'hidden',
    tags: [],
    search: ''
  });
  
  // Todo lists for SuperTaskToolbar
  const { data: todoLists = [] } = useTodoLists();
  
  // Confirmation dialog hook
  const { confirm } = useDialog();
  
  // Compute selected list
  const selectedList = todoLists.find(list => list.id === todoFilters.list) || null;

  // Handle push notification navigation
  const handlePushNotificationNavigation = useCallback((taskId: number, listId: number, _notificationType: string) => {
    // Navigate with task ID, list ID, and assignee filter in URL params to trigger highlighting in TodosMode
    // TodosMode will handle setting the filters to ensure the task is visible
    navigate(`/?taskId=${taskId}&highlight=true&listId=${listId}&assignee=any`);
  }, [navigate]);

  // PWA initialization and shortcut handling
  useEffect(() => {
    // Check for PWA shortcuts
    pwaService.checkForShortcuts();
    
    // Handle PWA shortcuts
    const handleNewTaskShortcut = () => {
      const addTaskButton = document.querySelector('[data-testid="add-task-button"]') as HTMLButtonElement;
      if (addTaskButton) {
        addTaskButton.click();
      }
    };
    
    const handleListsShortcut = () => {
      navigate('/');
    };
    
    // Handle push notification navigation
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data.type === 'NAVIGATE_TO_TASK') {
        const { taskId, listId, notificationType } = event.data.data;
        
        if (taskId && listId) {
          handlePushNotificationNavigation(taskId, listId, notificationType);
        }
      } else if (event.data.type === 'SW_UPDATED') {
        // Service worker updated - can show UI notification if needed
      }
    };
    
    window.addEventListener('pwa-shortcut-new-task', handleNewTaskShortcut);
    window.addEventListener('pwa-shortcut-lists', handleListsShortcut);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }
    
    return () => {
      window.removeEventListener('pwa-shortcut-new-task', handleNewTaskShortcut);
      window.removeEventListener('pwa-shortcut-lists', handleListsShortcut);
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [navigate, handlePushNotificationNavigation]);

  // Check for first-time user setup
  useEffect(() => {
    if (currentUser) {
      const hasSeenWelcome = localStorage.getItem(`symphonytodo-welcome-${currentUser.email}`);
      const neverShowAgain = localStorage.getItem('welcome-setup-never-show');
      
      if (!hasSeenWelcome && neverShowAgain !== 'true') {
        // Show welcome setup after a short delay to let the app load
        setTimeout(() => {
          setShowWelcomeSetup(true);
        }, 1000);
      }
    }
  }, [currentUser]);

  // Notification management
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    removeAllNotifications = () => {},
    isLoading,
    error,
  } = useNotifications(currentUser?.email || '') || {};

  // Received invitations management
  const { invitations } = useReceivedInvitations();

  // Feedback functionality
  const handleFeedbackSubmit = async (feedback: FeedbackData): Promise<void> => {
    await submitFeedback(feedback);
    alert(
      'success',
      'Feedback Submitted',
      'Thank you for your feedback! We\'ll review it and get back to you if needed.'
    );
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + F for feedback
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'F') {
        event.preventDefault();
        setShowFeedback(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // List management mutations
  const createListMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; color?: string }) => 
      todoApi.createList(data, currentUser?.email || ''),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowCreateListForm(false);
      setCreateListData(null);
      if (newList && newList.id) {
        setTodoFilters({ ...todoFilters, list: newList.id });
      }
    },
  });

  const updateListMutation = useMutation({
    mutationFn: async (data: { 
      name: string; 
      description?: string; 
      color?: string;
      isShared?: boolean;
      sharedWith?: string[];
      isPublic?: boolean;
    }) => {
      if (!selectedList) return null;
      
      // Check if this is the first time making the list public
      const isNewlyPublic = data.isPublic && !selectedList.isPublic;
      
      // First update basic list info
      const updatedList = await todoApi.updateList(selectedList.id, {
        name: data.name,
        description: data.description,
        color: data.color,
        isPublic: data.isPublic
      }, currentUser?.email || '');
      
      // Then update sharing if it changed
      const sharingChanged = data.isShared !== selectedList.isShared || 
        JSON.stringify(data.sharedWith) !== JSON.stringify(selectedList.sharedWith);
        
      if (sharingChanged) {
        await todoApi.updateListSharing(
          selectedList.id, 
          data.isShared || false, 
          data.sharedWith || [], 
          currentUser?.email || ''
        );
      }
      
      return { updatedList, isNewlyPublic };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      
      // Check if we generated a new public URL
      if (result?.isNewlyPublic && result.updatedList?.publicAccessToken) {
        const publicUrl = `${window.location.origin}/public/${result.updatedList.publicAccessToken}`;
        
        // Delay showing celebration modal to ensure edit modal closes first
        setTimeout(() => {
          setNewPublicUrl(publicUrl);
        }, 100);
      }
      
      setShowEditListForm(false);
      setEditListData(null);
    },
    onError: (error) => {
      console.error('List update failed:', error);
    },
  });

  const deleteListMutation = useMutation({
    mutationFn: async () => {
      if (!selectedList) return;
      const response = await fetch(`/api/TodoLists/${selectedList.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete list');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowEditListForm(false);
      setEditListData(null);
      setTodoFilters({ ...todoFilters, list: 'all' });
    },
  });

  const handleNotificationClick = (notification: Notification) => {
    if ('taskId' in notification) {
      const taskNotification = notification as TaskNotification;
      
      let assigneeFilter = 'any';
      
      if (taskNotification.type === 'task_assigned') {
        assigneeFilter = currentUser?.email || 'any';
      } else {
        assigneeFilter = 'any';
      }
      
      // Navigate with task ID, list ID, and assignee filter in URL params to trigger highlighting in TodosMode
      // TodosMode will handle setting the filters to ensure the task is visible
      navigate(`/?taskId=${taskNotification.taskId}&highlight=true&listId=${taskNotification.listId}&assignee=${assigneeFilter}`);
    }
    setShowNotifications(false);
  };

  const handleWelcomeComplete = () => {
    if (currentUser) {
      localStorage.setItem(`symphonytodo-welcome-${currentUser.email}`, 'true');
    }
    setShowWelcomeSetup(false);
  };

  if (!currentUser) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw'
      }}>
        <Loader type="bars" size="lg" />
      </div>
    );
  }

  return (
    <NavigationProvider>
      <TodoFilterProvider>
        <AppLayout 
          onShowNotifications={() => setShowNotifications(true)}
          onShowFeedback={() => setShowFeedback(true)}
        >
        <ReceivedInvitationsWidget
          invitations={invitations}
        />

        <Routes>
          <Route path="/*" element={<ModeRenderer />} />
          <Route path="/todos/*" element={<ModeRenderer />} />
          <Route path="/notes/*" element={<ModeRenderer />} />
          <Route path="/calendar/*" element={<ModeRenderer />} />
          <Route path="/kanban/*" element={<ModeRenderer />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>

        <NotificationPanel
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          notifications={notifications}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onNotificationClick={handleNotificationClick}
          onNotificationRemove={removeNotification}
          onRemoveAllNotifications={removeAllNotifications}
          isLoading={isLoading}
          error={error}
        />

        {navigationMessage && (
          <div style={{
            position: 'fixed',
            top: '80px',
            right: '20px',
            background: '#4f46e5',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {navigationMessage}
          </div>
        )}

        {/* List Management Modals */}
        <Modal
          isOpen={showCreateListForm}
          onClose={() => setShowCreateListForm(false)}
          title="Create New List"
          hideCloseButton={true}
          actionButtons={
            <>
              <button
                type="button"
                onClick={() => {
                  if (createListData && createListData.name.trim() && createListData.color) {
                    createListMutation.mutate({
                      name: createListData.name,
                      description: createListData.description,
                      color: createListData.color,
                    });
                  }
                }}
                disabled={!createListData?.name?.trim() || !createListData?.color || createListMutation.isPending}
                className="header-btn header-btn-save"
                title="Create list"
                aria-label="Create list"
              >
                <Check size={16} />
              </button>
              <button
                type="button"
                onClick={() => setShowCreateListForm(false)}
                className="header-btn header-btn-close"
                title="Cancel"
                aria-label="Cancel"
              >
                <X size={16} />
              </button>
            </>
          }
        >
          <CreateListForm
            onSave={setCreateListData}
          />
        </Modal>

        {selectedList && (
          <Modal
            isOpen={showEditListForm}
            onClose={() => setShowEditListForm(false)}
            title="Edit List"
            hideCloseButton={true}
            actionButtons={
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (editListData && editListData.name.trim()) {
                      updateListMutation.mutate({
                        name: editListData.name,
                        description: editListData.description,
                        color: editListData.color,
                        isShared: editListData.isShared,
                        sharedWith: editListData.sharedWith,
                        isPublic: editListData.isPublic,
                      });
                    }
                  }}
                  disabled={!editListData?.name.trim() || updateListMutation.isPending}
                  className="header-btn header-btn-save"
                  title="Save changes"
                  aria-label="Save changes"
                >
                  <Check size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    confirm(
                      'Delete List',
                      `Are you sure you want to delete "${selectedList.name}"? This will also delete all tasks in the list.`,
                      () => {
                        deleteListMutation.mutate();
                      }
                    );
                  }}
                  disabled={deleteListMutation.isPending}
                  className="header-btn header-btn-delete"
                  title="Delete list"
                  aria-label="Delete list"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditListForm(false)}
                  className="header-btn header-btn-close"
                  title="Cancel"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </>
            }
          >
            <EditListForm
              list={selectedList}
              onSave={setEditListData}
              onClose={() => setShowEditListForm(false)}
            />
          </Modal>
        )}

        {/* New Public URL Modal */}
        {newPublicUrl && (
          <Modal
            isOpen={!!newPublicUrl}
            onClose={() => setNewPublicUrl(null)}
            title="🚀 Public URL Generated!"
            hideCloseButton={false}
            actionButtons={
              <button
                type="button"
                onClick={() => setNewPublicUrl(null)}
                className="btn btn-primary"
              >
                Got it!
              </button>
            }
          >
            <div className="new-public-url-modal">
              <div className="success-message">
                <div className="success-icon">🎉</div>
                <p>Your list is now publicly shareable! Anyone with this link can view your list in read-only mode.</p>
              </div>
              
              <div className="public-url-share-section">
                <label>Public URL:</label>
                <div className="public-url-share-display">
                  <input
                    type="text"
                    value={newPublicUrl}
                    readOnly
                    className="public-url-share-input"
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary copy-url-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(newPublicUrl);
                    }}
                    title="Copy to clipboard"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div className="share-tips">
                <h4>💡 Sharing Tips:</h4>
                <ul>
                  <li>Share this URL with anyone to give them read-only access</li>
                  <li>They can view all items, comments, and progress</li>
                  <li>You can disable public sharing anytime in list settings</li>
                </ul>
              </div>
            </div>
          </Modal>
        )}

        {/* List Info Dialog for non-owners */}
        {selectedList && showListInfoDialog && (
          <ListInfoDialog
            list={selectedList}
            onClose={() => setShowListInfoDialog(false)}
          />
        )}

        {/* Welcome Setup Modal */}
        <WelcomeSetup
          isOpen={showWelcomeSetup}
          onClose={() => setShowWelcomeSetup(false)}
          onComplete={handleWelcomeComplete}
        />

        {/* Feedback System */}
        <FeedbackModal
          isOpen={showFeedback}
          onClose={() => setShowFeedback(false)}
          onSubmit={handleFeedbackSubmit}
        />
        
      </AppLayout>
      </TodoFilterProvider>
    </NavigationProvider>
  );
}



function PasswordResetPage() {
  const handleSuccess = () => {
    // Redirect to login after successful password reset
    window.location.href = '/';
  };

  return <NewPasswordForm onSuccess={handleSuccess} />;
}

function PublicTodoListPage() {
  const { token } = useParams<{ token: string }>();
  
  if (!token) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'var(--app-text-muted)'
      }}>
        Invalid public link
      </div>
    );
  }

  return <PublicTodoList token={token} />;
}

function PublicNoteViewPage() {
  const { token } = useParams<{ token: string }>();
  
  if (!token) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'var(--app-text-muted)'
      }}>
        Invalid public note link
      </div>
    );
  }

  return <PublicNoteView />;
}

function App() {
  return (
    <HelmetProvider>
      <UserProvider>
        <DialogProvider>
          <ToastProvider>
            <PremiumProvider>
              <SignalRProvider>
              <Routes>
              <Route
                path="/auth/new-password"
                element={<PasswordResetPage />}
              />
              <Route
                path="/public/:token"
                element={<PublicTodoListPage />}
              />
              <Route
                path="/public/note/:token"
                element={<PublicNoteViewPage />}
              />
              <Route
                path="*"
                element={
                  <AuthGuard>
                    <AppContent />
                  </AuthGuard>
                }
              />
              </Routes>
              </SignalRProvider>
            </PremiumProvider>
          </ToastProvider>
        </DialogProvider>
      </UserProvider>
    </HelmetProvider>
  );
}

export default App;
